import { schemas } from '@oneappexchange/oaf-schema';
import { StatusBadgeVariant } from 'wds/statusBadge';
import { FilterOption, FilterSection, L2FilterOption } from '~/components/ace/filterSection/filterSection';
import { FeatureFlags } from '~/modules/feature_flags';
import { isConsistentFiltersEnabled, isSlackListingsEnabled } from '~/modules/utilities/slackPilot';
import { PRIVATE_OFFER_PRICE_FILTER_VALUE } from '~/modules/helpers/privateOfferFilterHelper';
import {
    FDE_ACHIEVEMENT_FILTER_VALUE,
    isConsultantAchievementFilterEnabled,
    PARTNER_OF_YEAR_ACHIEVEMENT_FILTER_VALUE,
    SUMMIT_ACHIEVEMENT_FILTER_VALUE,
} from '~/modules/helpers/consultantAchievementFilterHelper';
import { BADGE_KEY_TO_VARIANT_MAP } from '~/modules/helpers/badgeHelper';
import { FilterLabels } from '~/modules/stores/searchFiltersStore';
import {
    EXTERNAL_AGENT_TECHNOLOGIES,
    EXTERNAL_AGENT_TECHNOLOGY_PLUGIN,
    EXTERNAL_AGENT_TECHNOLOGY_SKILL,
    getSkillDomainDisplayName,
    SKILL_DOMAINS,
    SUPPRESSED_SKILL_DOMAINS,
} from '~/modules/helpers/externalAgentSearchHelper';
import { FilterParams } from '~/modules/stores/searchStore';
import { getUniqueIteratorKey } from '~/modules/utilities/lwcUtilities';
import { i18nStore } from '../stores/i18nStore';
import { logger } from '../utilities/logger';

type SpecificFilterLabels = I18nMessages['search']['filters'];
type LocationLabels = I18nMessages['location'];
type OafLabels = I18nMessages['oaf'];
type ListingDetailsLabels = I18nMessages['listingDetails'];

export enum SectionNames {
    PRICE = 'price',
    RATING = 'rating',
    AGENTFORCE = 'agentforce',
    CATEGORY = 'category',
    TECH = 'tech',
    DOMAIN = 'domain',
    PRODUCT = 'product',
    SALESFORCE_LABS = 'labs',
    EDITION = 'edition',
    FEATURES = 'features',
    INDUSTRY = 'industry',
    PERSONA = 'persona',
    LANGUAGES = 'languages',
    IMPACT = 'impact',
    ACHIEVEMENT = 'achievement',
}

/**
 * API slug keys for the industryExpertise parent categories.
 * These must stay in lockstep with INDUSTRIES_EXPERTISE_MAP in appx-recommendations-service.
 */
export const INDUSTRY_EXPERTISE_SLUGS = [
    'agriculture',
    'automotive',
    'communications',
    'consumer',
    'education',
    'energy',
    'engineering',
    'financial',
    'health',
    'tech',
    'manufacturing',
    'media',
    'netzero',
    'nonprofit',
    'professional',
    'public',
    'retail',
    'travel',
] as const;

export type IndustryExpertiseSlug = (typeof INDUSTRY_EXPERTISE_SLUGS)[number];

const sortOptions = (filterOptions: FilterOption[], lastLabel?: string): FilterOption[] => {
    return filterOptions.slice().sort((a, b) => {
        if (lastLabel && a.label === lastLabel) {
            return 1;
        }
        // @ts-expect-error * Automated Type Error Fix
        return a.label.localeCompare(b.label);
    });
};

const agentforceContentToolTips = (listingDetailsLabels: ListingDetailsLabels) => {
    const badges = [];

    // Actions badge (first)
    badges.push({
        badge: {
            i18nFn: () => listingDetailsLabels.badges.numActions,
            i18nKey: 'actions',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: listingDetailsLabels.tooltipsText.numActions,
    });

    // Topics badge (Subagents) (second)
    badges.push({
        badge: {
            i18nFn: () => listingDetailsLabels.badges.numTopics,
            i18nKey: 'topics',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: listingDetailsLabels.tooltipsText.numTopics,
    });

    // Agents badge (third)
    badges.push({
        badge: {
            i18nFn: () => listingDetailsLabels.badges.numAgents,
            i18nKey: 'agents',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: listingDetailsLabels.tooltipsText.numAgents,
    });

    // MCP Servers badge (fourth)
    badges.push({
        badge: {
            i18nFn: () => listingDetailsLabels.badges.numMCPServers,
            i18nKey: 'mcpServers',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: listingDetailsLabels.tooltipsText.numMCPServers,
    });

    return badges;
};

const numberOfExperts = (): object[] => {
    const { i18n } = i18nStore.getState();
    const fromTo = [
        [1, 5],
        [6, 10],
        [11, 50],
        [51, 100],
        [101, 200],
        [201, 1000],
    ].map((option) => {
        const [from, to] = option;
        const label = i18n.search.filters.experts.fromValueToValue({ from, to });
        return { value: `${from}to${to}`, label: label };
    });
    return [...fromTo, { value: '1000', label: i18n.search.filters.experts.above1000() }];
};

const getOafSchemaFilterOptions = (
    labels: SpecificFilterLabels | OafLabels,
    schemaFilterOptions: string[],
    filterSection: string,
    excludedOptions: string[] = [],
) => {
    // @ts-expect-error * Automated Type Error Fix
    const knownLabelKeys = Object.keys(labels[filterSection]);
    return schemaFilterOptions.reduce((types: FilterOption[], type: string) => {
        if (!excludedOptions.includes(type) && knownLabelKeys.includes(type)) {
            types.push({
                // @ts-expect-error * Automated Type Error Fix
                label: labels[filterSection][type],
                value: type,
            });
        }
        return types;
    }, []);
};

const ratingOptions = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: labels.rating.label,
        name: SectionNames.RATING,
        multiple: false,
        selectedValue: filterParams?.rating,
        enableRadioDeselect: true,
        options: [
            { type: 'stars', label: labels.stars.fiveStars, value: '5' },
            { type: 'stars', label: labels.stars.fourStars, value: '4' },
            { type: 'stars', label: labels.stars.threeStars, value: '3' },
            { type: 'stars', label: labels.stars.twoStars, value: '2' },
            { type: 'stars', label: labels.stars.oneStar, value: '1' },
            { label: labels.stars.zeroStars, value: '0' },
        ],
    };
};

const ratingAppFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        ...ratingOptions(filterParams, labels),
        key: 'rating-apps',
    };
};
function pricingToolTips(labels: FilterLabels) {
    const freemiumBadge = {
        badge: {
            i18nFn: () => labels.filters.badges.freemium,
            i18nKey: 'freemium',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: labels.filters.tooltipsText.freemium,
    };

    const paidAddOnBadge = {
        badge: {
            i18nFn: () => labels.filters.badges.paidAddOn,
            i18nKey: 'paidAddOn',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: labels.filters.tooltipsText.paidAddOn,
    };

    const paidCheckoutBadge = {
        badge: {
            i18nFn: () => labels.filters.badges.paidCheckout,
            i18nKey: 'paidCheckout',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: labels.filters.tooltipsText.paidCheckout,
    };
    return [freemiumBadge, paidAddOnBadge, paidCheckoutBadge];
}

const priceAppFilterSectionOAF = (filterParams: FilterParams | undefined, labels: FilterLabels) => {
    const privateOfferEnabled = FeatureFlags.FF_PRIVATE_OFFER_FILTERS;
    const knownLabelKeys = Object.keys(labels.filters.price);
    const filterOptions: L2FilterOption[] = schemas['pricing_plan/Pricing']
        .get()
        .properties.price_model_type.enum.reduce((options: L2FilterOption[], type: string) => {
            try {
                if (knownLabelKeys.includes(type)) {
                    if (type === 'paid') {
                        options.push({
                            label: labels.filters.price[type],
                            value: type,
                            isL2Filter: true,
                            multiple: true,
                            options: [
                                ...(privateOfferEnabled
                                    ? [
                                          {
                                              label: labels.filters.price.privateOffer,
                                              value: PRIVATE_OFFER_PRICE_FILTER_VALUE,
                                              statusBadge: StatusBadgeVariant.New,
                                              statusBadgeSize: 'small' as const,
                                          },
                                      ]
                                    : []),
                                {
                                    label: labels.filters.price.paidcheckout,
                                    value: 'paidcheckout',
                                },
                            ],
                            toggleDefault: privateOfferEnabled,
                        });
                    } else {
                        options.push({
                            // @ts-expect-error * Automated Type Error Fix
                            label: labels.filters.price[type],
                            value: type,
                        });
                    }
                } else {
                    logger.warn(`No label found for price filter value ${type}`);
                }
            } catch (e) {
                logger.warn(`No label found for price filter value ${type}`);
            }
            return options;
        }, []);
    filterOptions.push({
        label: labels.filters.price.nonprofits,
        value: 'nonprofits',
    });

    return {
        label: labels.filters.price.label,
        name: SectionNames.PRICE,
        type: 'multiLevel',
        multiple: true,
        selectedValue: filterParams?.price,
        key: 'price-apps',
        options: filterOptions,
        tooltip: pricingToolTips(labels),
        tooltipPlacement: 'top',
    };
};

export const categoryOptionsOrder = [
    'sales',
    'marketing',
    'itAndAdministration',
    'customerService',
    'finance',
    'analytics',
    'productivity',
    'commerce',
    'collaboration',
    'enterpriseResourcePlanning',
];

type ListingCategorySchema = {
    properties: Record<
        string,
        {
            properties: { categories: { items: { enum: string[] } } };
        }
    >;
};

export const getCategoryFilterOrder = (labels: OafLabels): string[] => {
    const listingCategorySchema = schemas['listing/ListingCategories'].get() as ListingCategorySchema;
    const order: string[] = [];
    for (const categoryKey of categoryOptionsOrder) {
        order.push(categoryKey);
        const category = listingCategorySchema.properties[categoryKey];
        const subCategoryOptions = category.properties.categories.items.enum.map((subCategory) => ({
            label: (labels.category as Record<string, string>)[subCategory],
            value: subCategory,
        }));
        const sortedSubCategories = sortOptions(subCategoryOptions);
        for (const sub of sortedSubCategories) {
            order.push(sub.value as string);
        }
    }
    return order;
};

const categoryAppFilterSection = (
    filterParams: FilterParams | undefined,
    labels: OafLabels,
    fallbackToggleState = true,
) => {
    const categoryOptions = [];
    for (const categoryKey of categoryOptionsOrder) {
        // @ts-expect-error * Automated Type Error Fix
        const category = schemas['listing/ListingCategories'].get().properties[categoryKey];
        // @ts-expect-error * Automated Type Error Fix
        const subCategoryOptions = category.properties.categories.items.enum.map((subCategory) => {
            return {
                // @ts-expect-error * Automated Type Error Fix
                label: labels.category[subCategory],
                value: subCategory,
            };
        });
        const sortedSubCategoryOptions = sortOptions(subCategoryOptions);
        categoryOptions.push({
            // @ts-expect-error * Automated Type Error Fix
            label: labels.category[categoryKey],
            value: categoryKey,
            isL2Filter: true,
            multiple: true,
            options: sortedSubCategoryOptions,
        });
    }
    return {
        label: labels.category.label,
        name: SectionNames.CATEGORY,
        type: 'multiLevel',
        multiple: true,
        selectedValue: filterParams?.category,
        toggleDefault: !!filterParams?.category || fallbackToggleState,
        key: 'category-apps',
        options: categoryOptions,
    };
};

const agentForceFilterSection = (
    filterParams: FilterParams | undefined,
    labels: OafLabels,
    listingDetails: ListingDetailsLabels,
    fallbackToggleState = false,
) => {
    const filterSection = {
        label: labels.agenticDetails.label,
        name: 'agenticContent',
        multiple: true,
        selectedValue: filterParams?.agenticContent || [],
        toggleDefault: !!filterParams?.agenticContent || fallbackToggleState,
        key: 'agentic-content-section',
        icon: 'agentforce',
        iconAlt: labels.agenticDetails.agentIconAltText,
        tooltip: agentforceContentToolTips(listingDetails), //tooltip badges
        tooltipPlacement: 'top',
        showTopTooltip: true,
        options: [
            {
                label: labels.agenticDetails.filterTools,
                value: 'actions',
            },
            {
                label: labels.agenticDetails.filterSubagents,
                value: 'topics',
            },
            { label: listingDetails.badges.numAgents, value: 'agents' },
        ],
    };

    filterSection.options.push({
        label: listingDetails.badges.numMCPServers,
        value: 'mcpServers',
    });

    return filterSection;
};

const techAppFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    fallbackToggleState = false,
) => {
    const schemaFilterOptions = schemas['listing/BaseListing'].get().properties.technology.enum;
    const excludedListingTechnologies = [
        'quip/package',
        'consultant',
        'slack',
        // External Agents are standalone resources, not Salesforce technologies.
        ...EXTERNAL_AGENT_TECHNOLOGIES,
    ];

    const result = {
        label: labels.solutionType.label,
        name: SectionNames.TECH,
        multiple: true,
        selectedValue: filterParams?.tech || [],
        toggleDefault: !!filterParams?.tech || fallbackToggleState,
        key: 'tech-apps',
        options: getOafSchemaFilterOptions(labels, schemaFilterOptions, 'solutionType', excludedListingTechnologies),
    };

    result.options = sortOptions(result.options);
    return result;
};

const externalAgentFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
): FilterSection => ({
    label: labels.externalAgentType.label,
    name: SectionNames.TECH,
    multiple: true,
    selectedValue: filterParams?.tech || [],
    toggleDefault: true,
    key: 'external-agent-tech',
    options: [
        {
            label: labels.externalAgentType.skills,
            value: EXTERNAL_AGENT_TECHNOLOGY_SKILL,
        },
        {
            label: labels.externalAgentType.plugins,
            value: EXTERNAL_AGENT_TECHNOLOGY_PLUGIN,
        },
    ],
});

const domainFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels): FilterSection => {
    const selectedDomains = new Set(filterParams?.domain);

    return {
        label: labels.domain.label,
        name: SectionNames.DOMAIN,
        multiple: true,
        selectedValue: filterParams?.domain || [],
        toggleDefault: true,
        key: 'external-agent-domain',
        options: SKILL_DOMAINS.filter(
            (domain) => !SUPPRESSED_SKILL_DOMAINS.has(domain) || selectedDomains.has(domain),
        ).map((domain) => ({
            label: getSkillDomainDisplayName(domain),
            value: domain,
        })),
    };
};

const productAppFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    fallbackToggleState = false,
) => {
    const schemaFilterOptions =
        schemas['listing/extensions/force/listings/Listing'].get().properties.productsSupported.items.enum;
    const excludedListingProducts = [];
    if (!FeatureFlags.FF_SHOW_TABLEAU_NEXT_ON_SITE_UI) {
        excludedListingProducts.push('Tableau Next');
    }

    const filterOptions = getOafSchemaFilterOptions(labels, schemaFilterOptions, 'product', excludedListingProducts);
    const sortedFilterOptions = sortOptions(filterOptions);
    sortedFilterOptions.unshift({ label: labels.product.any, value: 'any' });
    return {
        label: labels.product.label,
        name: SectionNames.PRODUCT,
        multiple: false,
        selectedValue: filterParams?.product,
        toggleDefault: !!filterParams?.product || fallbackToggleState,
        key: 'product',
        options: sortedFilterOptions,
        enableRadioDeselect: true,
    };
};

const productExploreFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    fallbackToggleState = false,
) => {
    const filterSection = productAppFilterSection(filterParams, labels, fallbackToggleState);
    filterSection.options = filterSection.options.filter((option) => !option.value.includes('any'));
    return filterSection;
};

const featuresAppFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: labels.features.label,
        name: SectionNames.FEATURES,
        multiple: true,
        selectedValue: filterParams?.features || [],
        toggleDefault: !!filterParams?.features,
        key: 'feature-apps',
        options: [
            { label: labels.features.fedRAMP, value: 'fedRAMP' },
            {
                label: labels.features.lightningAppBuilder,
                value: 'lightningAppBuilder',
            },
            { label: labels.features.lightningReady, value: 'lightningReady' },
            { label: labels.features.managedPackage, value: 'managedPackage' },
            { label: labels.features.multiCurrency, value: 'multiCurrency' },
            { label: labels.features.personAccounts, value: 'personAccounts' },
            { label: labels.features.mobile, value: 'mobile' },
            { label: labels.features.native, value: 'native' },
            { label: labels.features.shield, value: 'shield' },
            ...(FeatureFlags.FF_SALES_PROGRAM_FILTER
                ? [{ label: labels.features.salesPrograms, value: 'salesPrograms' }]
                : []),
            { label: labels.features.isEinsteinAgent, value: 'isEinsteinAgent' },
        ],
    };
};

const editionAppFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    const schemaFilterOptions =
        schemas['listing/extensions/force/listings/Listing'].get().properties.editions.items.enum;
    const filterOptions = getOafSchemaFilterOptions(labels, schemaFilterOptions, 'edition');
    const sortedOptions = sortOptions(filterOptions);
    return {
        label: labels.edition.label,
        name: SectionNames.EDITION,
        multiple: true,
        selectedValue: filterParams?.edition,
        toggleDefault: !!filterParams?.edition,
        key: 'edition-apps',
        options: sortedOptions,
    };
};

const languageFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    const filter = {
        label: labels.language.label,
        name: SectionNames.LANGUAGES,
        multiple: true,
        selectedValue: filterParams?.languages,
        toggleDefault: !!filterParams?.languages,
        key: 'languages-apps',
        options: [] as FilterOption[],
    };
    const schemaFilterOptions =
        schemas['listing/extensions/force/listings/Listing'].get().properties.languages.items.enum;

    filter.options = getOafSchemaFilterOptions(labels, schemaFilterOptions, 'language');
    filter.options = filter.options.sort((a, b) => a.label?.localeCompare(b.label || '') || -1);
    return filter;
};

const industryFilterSection = (
    filterParams: FilterParams | undefined,
    labels: FilterLabels,
    fallbackToggleState = false,
) => {
    const schemaFilterOptions =
        schemas['listing/extensions/force/listings/Listing'].get().properties.supportedIndustries.items.enum;
    const options = getOafSchemaFilterOptions(labels.oaf, schemaFilterOptions, 'supportedIndustries');
    return {
        label: labels.filters.supportedIndustries.label,
        name: SectionNames.INDUSTRY,
        multiple: true,
        selectedValue: filterParams?.industry || [],
        toggleDefault: !!filterParams?.industry || fallbackToggleState,
        key: 'industry',
        options: sortOptions(options),
    };
};

const targetUserPersonaFilterSection = (filterParams: FilterParams | undefined, labels: FilterLabels) => {
    const schemaFilterOptions =
        schemas['listing/extensions/force/listings/Listing'].get().properties.targetUserPersona.items.enum;
    const options = getOafSchemaFilterOptions(labels.oaf, schemaFilterOptions, 'targetUserPersona');
    return {
        label: labels.filters.targetUserPersona.label,
        name: SectionNames.PERSONA,
        multiple: true,
        selectedValue: filterParams?.persona || [],
        toggleDefault: !!filterParams?.persona,
        key: 'persona',
        options: sortOptions(options, labels.oaf.targetUserPersona.Other),
    };
};

function impactFilterOptions(labels: SpecificFilterLabels, type: string) {
    const consultantOptions = [
        { label: labels.impact.diverse, value: 'diverse' },
        { label: labels.impact.pledge, value: 'pledge' },
    ];
    return type === 'apps'
        ? [{ label: labels.impact.accessible, value: 'accessible' }, ...consultantOptions]
        : consultantOptions;
}

function labsFilterOptions(labels: SpecificFilterLabels) {
    return [{ label: labels.labs.salesforceLabs, value: 'salesforceLabs' }];
}

function impactToolTips(labels: SpecificFilterLabels, type: string) {
    const pledgeBadge = {
        badge: {
            i18nFn: () => labels.badges.isPledge1Percent,
            i18nKey: 'isPledge1Percent',
            variant: BADGE_KEY_TO_VARIANT_MAP.isPledge1Percent,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: labels.tooltipsText.isPledge1Percent,
    };

    const accessibleBadge = {
        badge: {
            i18nFn: () => labels.badges.isAccessibleSolution,
            i18nKey: 'isAccessibleSolution',
            variant: BADGE_KEY_TO_VARIANT_MAP.Default,
            iteratorKey: getUniqueIteratorKey(),
        },
        description: labels.tooltipsText.isAccessibleSolution,
    };
    return type === 'apps' ? [pledgeBadge, accessibleBadge] : [pledgeBadge];
}

const impactAppFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    type: string,
    fallbackToggleState = false,
) => {
    return {
        label: labels.impact.label,
        name: SectionNames.IMPACT,
        multiple: true,
        selectedValue: filterParams?.impact || [],
        toggleDefault: !!filterParams?.impact || fallbackToggleState,
        key: `impact-${type}`,
        options: impactFilterOptions(labels, type),
        tooltip: impactToolTips(labels, type),
        tooltipPlacement: 'top',
    };
};

const impactAppExploreFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    type: string,
    fallbackToggleState = false,
) => {
    return {
        label: labels.impact.label,
        name: 'impact',
        multiple: true,
        selectedValue: filterParams?.impact || [],
        toggleDefault: !!filterParams?.impact || fallbackToggleState,
        key: `impact-${type}`,
        options: impactFilterOptions(labels, type),
        tooltip: impactToolTips(labels, type),
        tooltipPlacement: 'bottom',
        tooltipLabel: labels.impact.tooltip,
    };
};

const labsFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    fallbackToggleState = false,
) => {
    return {
        label: labels.labs.label,
        name: SectionNames.SALESFORCE_LABS,
        multiple: true,
        selectedValue: filterParams?.labs,
        toggleDefault: !!filterParams?.labs || fallbackToggleState,
        key: `labs`,
        options: labsFilterOptions(labels),
    };
};

const sfExpertiseConsultantFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    if (!isConsistentFiltersEnabled()) {
        return {
            label: labels.sfExpertise.labelLegacy,
            name: 'sfExpertise',
            multiple: true,
            selectedValue: filterParams?.sfExpertise,
            key: 'sfExpertise-consultant',
            options: [
                { label: labels.sfExpertise.b2cCommerce, value: 'B2C Commerce' },
                { label: labels.sfExpertise.commerce, value: 'Commerce' },
                {
                    label: labels.sfExpertise.customer360Plat,
                    value: 'Customer 360 Platform',
                },
                { label: labels.sfExpertise.educationCloud, value: 'Education Cloud' },
                { label: labels.sfExpertise.einstein, value: 'Einstein' },
                { label: labels.sfExpertise.expCloud, value: 'Experience Cloud' },
                { label: labels.sfExpertise.indProduct, value: 'Industry Products' },
                { label: labels.sfExpertise.integration, value: 'Integration' },
                { label: labels.sfExpertise.managedService, value: 'Managed Services' },
                {
                    label: labels.sfExpertise.mktCloud,
                    value: 'Marketing Cloud Engagement',
                },
                {
                    label: labels.sfExpertise.multiIntegration,
                    value: 'Multi-Cloud Integration',
                },
                {
                    label: labels.sfExpertise.nonprofitCloud,
                    value: 'Nonprofit Solutions',
                },
                { label: labels.sfExpertise.pdo, value: 'PDO/AppExchange' },
                { label: labels.sfExpertise.revenueCloud, value: 'Revenue Cloud' },
                { label: labels.sfExpertise.salesCloud, value: 'Sales Cloud' },
                { label: labels.sfExpertise.serviceCloud, value: 'Service Cloud' },
            ],
        };
    }
    return {
        label: labels.sfExpertise.label,
        name: 'sfExpertise',
        type: 'multiLevel',
        multiple: true,
        selectedValue: filterParams?.sfExpertise,
        key: 'sfExpertise-consultant',
        options: [
            {
                label: labels.sfExpertise.agentforceCategory,
                value: 'Agentforce',
                isL2Filter: true,
                multiple: true,
                options: [{ label: labels.sfExpertise.agentforce, value: 'Agentforce' }],
            },
            {
                label: labels.sfExpertise.commerceCategory,
                value: 'Agentforce Commerce',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.sfExpertise.b2bCommerce, value: 'B2B Commerce' },
                    { label: labels.sfExpertise.b2cCommerce, value: 'B2C Commerce' },
                    { label: labels.sfExpertise.commerceAI, value: 'Commerce AI' },
                    {
                        label: labels.sfExpertise.orderManagement,
                        value: 'Order Management',
                    },
                    { label: labels.sfExpertise.payments, value: 'Payments' },
                ],
            },
            {
                label: labels.sfExpertise.marketingCategory,
                value: 'Agentforce Marketing',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.sfExpertise.b2bMarketingAutomation,
                        value: 'B2B Marketing Automation',
                    },
                    {
                        label: labels.sfExpertise.emailMarketingMobileAdvertising,
                        value: 'Email Marketing, Mobile, and Advertising',
                    },
                    {
                        label: labels.sfExpertise.loyaltyManagement,
                        value: 'Loyalty Management',
                    },
                    {
                        label: labels.sfExpertise.marketingAnalytics,
                        value: 'Marketing Analytics',
                    },
                    {
                        label: labels.sfExpertise.personalization,
                        value: 'Personalization',
                    },
                ],
            },
            {
                label: labels.sfExpertise.salesCategory,
                value: 'Agentforce Sales',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.sfExpertise.cpq, value: 'CPQ' },
                    { label: labels.sfExpertise.partnerCloud, value: 'Partner Cloud' },
                    {
                        label: labels.sfExpertise.revenueLifecycleManagement,
                        value: 'Revenue Lifecycle Management',
                    },
                    { label: labels.sfExpertise.salesAgents, value: 'Sales Agents' },
                    { label: labels.sfExpertise.salesAI, value: 'Sales AI' },
                    { label: labels.sfExpertise.salesCloud, value: 'Sales Cloud' },
                    {
                        label: labels.sfExpertise.salesPerformanceManagement,
                        value: 'Sales Performance Management',
                    },
                ],
            },
            {
                label: labels.sfExpertise.serviceCategory,
                value: 'Agentforce Service',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.sfExpertise.contactCenter, value: 'Contact Center' },
                    {
                        label: labels.sfExpertise.customerServiceAI,
                        value: 'Customer Service AI',
                    },
                    {
                        label: labels.sfExpertise.customerServiceManagement,
                        value: 'Customer Service Management',
                    },
                    { label: labels.sfExpertise.fieldService, value: 'Field Service' },
                    {
                        label: labels.sfExpertise.serviceAnalytics,
                        value: 'Service Analytics',
                    },
                ],
            },
            {
                label: labels.sfExpertise.analyticsCategory,
                value: 'Analytics',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.sfExpertise.crmAnalytics, value: 'CRM Analytics' },
                    { label: labels.sfExpertise.tableau, value: 'Tableau' },
                    { label: labels.sfExpertise.tableauNext, value: 'Tableau Next' },
                    {
                        label: labels.sfExpertise.tableauSemantics,
                        value: 'Tableau Semantics',
                    },
                ],
            },
            {
                label: labels.sfExpertise.data360Category,
                value: 'Data 360',
                isL2Filter: true,
                multiple: true,
                options: [{ label: labels.sfExpertise.data360, value: 'Data 360' }],
            },
            {
                label: labels.sfExpertise.platformCategory,
                value: 'Platform',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.sfExpertise.agentforceAIAppDevelopment,
                        value: 'Agentforce and AI App Development',
                    },
                    {
                        label: labels.sfExpertise.artificialIntelligence,
                        value: 'Artificial Intelligence',
                    },
                    {
                        label: labels.sfExpertise.dataSecurityPrivacy,
                        value: 'Data Security and Privacy',
                    },
                    {
                        label: labels.sfExpertise.herokuArchitect,
                        value: 'Heroku Architect',
                    },
                    {
                        label: labels.sfExpertise.herokuDeveloper,
                        value: 'Heroku Developer',
                    },
                    { label: labels.sfExpertise.integration, value: 'Integration' },
                    {
                        label: labels.sfExpertise.mulesoftAnypoint,
                        value: 'MuleSoft Anypoint Platform',
                    },
                    {
                        label: labels.sfExpertise.mulesoftAutomation,
                        value: 'MuleSoft Automation',
                    },
                ],
            },
            {
                label: labels.sfExpertise.slackCategory,
                value: 'Slack',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.sfExpertise.agentforceInSlack,
                        value: 'Agentforce in Slack',
                    },
                    { label: labels.sfExpertise.slack, value: 'Slack' },
                ],
            },
        ],
    };
};

const industryExpertiseConsultantFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
) => {
    if (!isConsistentFiltersEnabled()) {
        return {
            label: labels.industryExpertise.labelLegacy,
            name: 'industryExpertise',
            multiple: true,
            selectedValue: filterParams?.industryExpertise,
            key: 'industryExpertise-consultant',
            options: [
                {
                    label: labels.industryExpertise.agricultureLegacy,
                    value: 'agriculture',
                },
                { label: labels.industryExpertise.automotive, value: 'automotive' },
                {
                    label: labels.industryExpertise.communications,
                    value: 'communications',
                },
                { label: labels.industryExpertise.consumer, value: 'consumer' },
                { label: labels.industryExpertise.education, value: 'education' },
                { label: labels.industryExpertise.energy, value: 'energy' },
                {
                    label: labels.industryExpertise.engineeringLegacy,
                    value: 'engineering',
                },
                { label: labels.industryExpertise.financial, value: 'financial' },
                { label: labels.industryExpertise.health, value: 'health' },
                { label: labels.industryExpertise.tech, value: 'tech' },
                {
                    label: labels.industryExpertise.manufacturing,
                    value: 'manufacturing',
                },
                { label: labels.industryExpertise.mediaLegacy, value: 'media' },
                { label: labels.industryExpertise.professional, value: 'professional' },
                { label: labels.industryExpertise.public, value: 'public' },
                { label: labels.industryExpertise.retail, value: 'retail' },
                { label: labels.industryExpertise.org, value: 'org' },
                {
                    label: labels.industryExpertise.travelTransportationHospitalityLegacy,
                    value: 'travel',
                },
            ],
        };
    }
    return {
        label: labels.industryExpertise.label,
        name: 'industryExpertise',
        type: 'multiLevel',
        multiple: true,
        selectedValue: filterParams?.industryExpertise,
        key: 'industryExpertise-consultant',
        options: [
            {
                label: labels.industryExpertise.agricultureMining,
                value: 'agriculture',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.industryExpertise.agriculture, value: 'agri' },
                    { label: labels.industryExpertise.mining, value: 'mining' },
                ],
            },
            {
                label: labels.industryExpertise.automotive,
                value: 'automotive',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.industryExpertise.dealers, value: 'dealers' },
                    { label: labels.industryExpertise.oems, value: 'oems' },
                ],
            },
            {
                label: labels.industryExpertise.communications,
                value: 'communications',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.communicationsCloud,
                        value: 'communicationscloud',
                    },
                ],
            },
            {
                label: labels.industryExpertise.consumer,
                value: 'consumer',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.consumerGoodsOther,
                        value: 'consumergoods',
                    },
                    {
                        label: labels.industryExpertise.consumerGoodsCloudSales,
                        value: 'consumergoodssales',
                    },
                    {
                        label: labels.industryExpertise.consumerGoodsCloudService,
                        value: 'consumergoodsservice',
                    },
                    {
                        label: labels.industryExpertise.customerPlanningForecasting,
                        value: 'customerplanning',
                    },
                    {
                        label: labels.industryExpertise.retailExecution,
                        value: 'retailexecution',
                    },
                    {
                        label: labels.industryExpertise.tradePromotionMgmt,
                        value: 'tradepromotion',
                    },
                ],
            },
            {
                label: labels.industryExpertise.education,
                value: 'education',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.collegesUniversities,
                        value: 'colleges',
                    },
                    {
                        label: labels.industryExpertise.educationOther,
                        value: 'educationother',
                    },
                    { label: labels.industryExpertise.k12, value: 'k12' },
                    {
                        label: labels.industryExpertise.technicalCommunityColleges,
                        value: 'community',
                    },
                ],
            },
            {
                label: labels.industryExpertise.energyUtilities,
                value: 'energy',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.energyUtilitiesCloud,
                        value: 'energycloud',
                    },
                ],
            },
            {
                label: labels.industryExpertise.engineeringConstructionRealEstate,
                value: 'engineering',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.construction,
                        value: 'construction',
                    },
                    { label: labels.industryExpertise.engineering, value: 'engg' },
                    { label: labels.industryExpertise.realEstate, value: 'realestate' },
                ],
            },
            {
                label: labels.industryExpertise.financial,
                value: 'financial',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.financialServicesCloud,
                        value: 'financialcloud',
                    },
                    { label: labels.industryExpertise.insurance, value: 'insurance' },
                ],
            },
            {
                label: labels.industryExpertise.healthcareLifeSciences,
                value: 'health',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.industryExpertise.healthCloud, value: 'healthcloud' },
                    { label: labels.industryExpertise.healthPlans, value: 'healthplans' },
                    {
                        label: labels.industryExpertise.medicalDevicesDiagnostics,
                        value: 'diagnostics',
                    },
                    { label: labels.industryExpertise.pharma, value: 'pharma' },
                    { label: labels.industryExpertise.providers, value: 'providers' },
                ],
            },
            {
                label: labels.industryExpertise.tech,
                value: 'tech',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.hardwareManufacturing,
                        value: 'hardware',
                    },
                    {
                        label: labels.industryExpertise.semiconductorComponents,
                        value: 'semiconductor',
                    },
                    {
                        label: labels.industryExpertise.softwareServices,
                        value: 'software',
                    },
                ],
            },
            {
                label: labels.industryExpertise.manufacturing,
                value: 'manufacturing',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.discreteManufacturing,
                        value: 'discrete',
                    },
                    {
                        label: labels.industryExpertise.manufacturingCloud,
                        value: 'manufacturingcloud',
                    },
                    {
                        label: labels.industryExpertise.processManufacturing,
                        value: 'process',
                    },
                ],
            },
            {
                label: labels.industryExpertise.media,
                value: 'media',
                isL2Filter: true,
                multiple: true,
                options: [{ label: labels.industryExpertise.mediaCloud, value: 'mediacloud' }],
            },
            {
                label: labels.industryExpertise.netZero,
                value: 'netzero',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.netZeroCloud,
                        value: 'netzerocloud',
                    },
                ],
            },
            {
                label: labels.industryExpertise.nonprofit,
                value: 'nonprofit',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.affiliationMembership,
                        value: 'affiliation',
                    },
                    {
                        label: labels.industryExpertise.environmentNatureEnergy,
                        value: 'environment',
                    },
                    {
                        label: labels.industryExpertise.nonprofitOther,
                        value: 'nonprofitother',
                    },
                    { label: labels.industryExpertise.socialServices, value: 'social' },
                ],
            },
            {
                label: labels.industryExpertise.professional,
                value: 'professional',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.businessServices,
                        value: 'business',
                    },
                    {
                        label: labels.industryExpertise.engineeringAccountingResearch,
                        value: 'earms',
                    },
                    {
                        label: labels.industryExpertise.otherServices,
                        value: 'otherservices',
                    },
                ],
            },
            {
                label: labels.industryExpertise.publicSectorGovernment,
                value: 'public',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.federalExpertise,
                        value: 'federal',
                    },
                    {
                        label: labels.industryExpertise.publicSectorSolutions,
                        value: 'pss',
                    },
                ],
            },
            {
                label: labels.industryExpertise.retail,
                value: 'retail',
                isL2Filter: true,
                multiple: true,
                options: [
                    {
                        label: labels.industryExpertise.foodDrugConvenienceGroceryRestaurants,
                        value: 'food',
                    },
                    {
                        label: labels.industryExpertise.massMerchantsDeptStoresERetail,
                        value: 'stores',
                    },
                    { label: labels.industryExpertise.specialtyLuxury, value: 'luxury' },
                ],
            },
            {
                label: labels.industryExpertise.travelTransportationHospitality,
                value: 'travel',
                isL2Filter: true,
                multiple: true,
                options: [
                    { label: labels.industryExpertise.hospitality, value: 'hospitality' },
                    {
                        label: labels.industryExpertise.transportation,
                        value: 'transportation',
                    },
                    { label: labels.industryExpertise.travel, value: 'travels' },
                ],
            },
        ],
    };
};

const noOfExpertsConsultantFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: isConsistentFiltersEnabled() ? labels.experts.label : labels.experts.labelLegacy,
        name: 'experts',
        multiple: true,
        selectedValue: filterParams?.experts,
        options: numberOfExperts(),
        key: 'experts-consultant',
    };
};

const locationConsultantFilterSection = (
    filterParams: FilterParams | undefined,
    labels: SpecificFilterLabels,
    locationLabels: LocationLabels,
) => {
    return {
        label: labels.location.label,
        name: 'location',
        type: 'location',
        selectedValue: filterParams?.location,
        key: 'location-consultant',
        options: [
            { label: labels.location.any, value: '', default: true },
            { label: '--------', value: '', disabled: true },
            {
                label: locationLabels.countries.US,
                value: 'US',
                states: [
                    { label: labels.location.choose, value: '*', default: true },
                    { value: 'AL', label: locationLabels.usStates.AL },
                    { value: 'AK', label: locationLabels.usStates.AK },
                    { value: 'AZ', label: locationLabels.usStates.AZ },
                    { value: 'AR', label: locationLabels.usStates.AR },
                    { value: 'CA', label: locationLabels.usStates.CA },
                    { value: 'CO', label: locationLabels.usStates.CO },
                    { value: 'CT', label: locationLabels.usStates.CT },
                    { value: 'DE', label: locationLabels.usStates.DE },
                    { value: 'DC', label: locationLabels.usStates.DC },
                    { value: 'FL', label: locationLabels.usStates.FL },
                    { value: 'GA', label: locationLabels.usStates.GA },
                    { value: 'HI', label: locationLabels.usStates.HI },
                    { value: 'ID', label: locationLabels.usStates.ID },
                    { value: 'IL', label: locationLabels.usStates.IL },
                    { value: 'IN', label: locationLabels.usStates.IN },
                    { value: 'IA', label: locationLabels.usStates.IA },
                    { value: 'KS', label: locationLabels.usStates.KS },
                    { value: 'KY', label: locationLabels.usStates.KY },
                    { value: 'LA', label: locationLabels.usStates.LA },
                    { value: 'ME', label: locationLabels.usStates.ME },
                    { value: 'MD', label: locationLabels.usStates.MD },
                    { value: 'MA', label: locationLabels.usStates.MA },
                    { value: 'MI', label: locationLabels.usStates.MI },
                    { value: 'MN', label: locationLabels.usStates.MN },
                    { value: 'MS', label: locationLabels.usStates.MS },
                    { value: 'MO', label: locationLabels.usStates.MO },
                    { value: 'MT', label: locationLabels.usStates.MT },
                    { value: 'NE', label: locationLabels.usStates.NE },
                    { value: 'NV', label: locationLabels.usStates.NV },
                    { value: 'NH', label: locationLabels.usStates.NH },
                    { value: 'NJ', label: locationLabels.usStates.NJ },
                    { value: 'NM', label: locationLabels.usStates.NM },
                    { value: 'NY', label: locationLabels.usStates.NY },
                    { value: 'NC', label: locationLabels.usStates.NC },
                    { value: 'ND', label: locationLabels.usStates.ND },
                    { value: 'OH', label: locationLabels.usStates.OH },
                    { value: 'OK', label: locationLabels.usStates.OK },
                    { value: 'OR', label: locationLabels.usStates.OR },
                    { value: 'PA', label: locationLabels.usStates.PA },
                    { value: 'RI', label: locationLabels.usStates.RI },
                    { value: 'SC', label: locationLabels.usStates.SC },
                    { value: 'SD', label: locationLabels.usStates.SD },
                    { value: 'TN', label: locationLabels.usStates.TN },
                    { value: 'TX', label: locationLabels.usStates.TX },
                    { value: 'UT', label: locationLabels.usStates.UT },
                    { value: 'VT', label: locationLabels.usStates.VT },
                    { value: 'VA', label: locationLabels.usStates.VA },
                    { value: 'WA', label: locationLabels.usStates.WA },
                    { value: 'WV', label: locationLabels.usStates.WV },
                    { value: 'WI', label: locationLabels.usStates.WI },
                    { value: 'WY', label: locationLabels.usStates.WY },
                ],
            },
            {
                label: locationLabels.countries.CA,
                value: 'CA',
                states: [
                    { label: labels.location.choose, value: '*', default: true },
                    { value: 'AB', label: locationLabels.caStates.AB },
                    { value: 'BC', label: locationLabels.caStates.BC },
                    { value: 'MB', label: locationLabels.caStates.MB },
                    { value: 'NB', label: locationLabels.caStates.NB },
                    { value: 'NL', label: locationLabels.caStates.NL },
                    { value: 'NT', label: locationLabels.caStates.NT },
                    { value: 'NS', label: locationLabels.caStates.NS },
                    { value: 'NU', label: locationLabels.caStates.NU },
                    { value: 'ON', label: locationLabels.caStates.ON },
                    { value: 'PE', label: locationLabels.caStates.PE },
                    { value: 'QC', label: locationLabels.caStates.QC },
                    { value: 'SK', label: locationLabels.caStates.SK },
                    { value: 'YT', label: locationLabels.caStates.YT },
                ],
            },
            { label: '--------', value: '', disabled: true },
            { value: 'AF', label: locationLabels.countries.AF },
            { value: 'AX', label: locationLabels.countries.AX },
            { value: 'AL', label: locationLabels.countries.AL },
            { value: 'DZ', label: locationLabels.countries.DZ },
            { value: 'AD', label: locationLabels.countries.AD },
            { value: 'AO', label: locationLabels.countries.AO },
            { value: 'AI', label: locationLabels.countries.AI },
            { value: 'AQ', label: locationLabels.countries.AQ },
            { value: 'AR', label: locationLabels.countries.AR },
            { value: 'AM', label: locationLabels.countries.AM },
            { value: 'AW', label: locationLabels.countries.AW },
            { value: 'AU', label: locationLabels.countries.AU },
            { value: 'AT', label: locationLabels.countries.AT },
            { value: 'AZ', label: locationLabels.countries.AZ },
            { value: 'BS', label: locationLabels.countries.BS },
            { value: 'BH', label: locationLabels.countries.BH },
            { value: 'BD', label: locationLabels.countries.BD },
            { value: 'BB', label: locationLabels.countries.BB },
            { value: 'BY', label: locationLabels.countries.BY },
            { value: 'BE', label: locationLabels.countries.BE },
            { value: 'BZ', label: locationLabels.countries.BZ },
            { value: 'BJ', label: locationLabels.countries.BJ },
            { value: 'BM', label: locationLabels.countries.BM },
            { value: 'BT', label: locationLabels.countries.BT },
            { value: 'BO', label: locationLabels.countries.BO },
            { value: 'BA', label: locationLabels.countries.BA },
            { value: 'BW', label: locationLabels.countries.BW },
            { value: 'BR', label: locationLabels.countries.BR },
            { value: 'BN', label: locationLabels.countries.BN },
            { value: 'BG', label: locationLabels.countries.BG },
            { value: 'BF', label: locationLabels.countries.BF },
            { value: 'BI', label: locationLabels.countries.BI },
            { value: 'KH', label: locationLabels.countries.KH },
            { value: 'CM', label: locationLabels.countries.CM },
            { value: 'CV', label: locationLabels.countries.CV },
            { value: 'CF', label: locationLabels.countries.CF },
            { value: 'TD', label: locationLabels.countries.TD },
            { value: 'CL', label: locationLabels.countries.CL },
            { value: 'CN', label: locationLabels.countries.CN },
            { value: 'CO', label: locationLabels.countries.CO },
            { value: 'KM', label: locationLabels.countries.KM },
            { value: 'CG', label: locationLabels.countries.CG },
            { value: 'CK', label: locationLabels.countries.CK },
            { value: 'CR', label: locationLabels.countries.CR },
            { value: 'CI', label: locationLabels.countries.CI },
            { value: 'HR', label: locationLabels.countries.HR },
            { value: 'CW', label: locationLabels.countries.CW },
            { value: 'CY', label: locationLabels.countries.CY },
            { value: 'CZ', label: locationLabels.countries.CZ },
            { value: 'CD', label: locationLabels.countries.CD },
            { value: 'DK', label: locationLabels.countries.DK },
            { value: 'DM', label: locationLabels.countries.DM },
            { value: 'DO', label: locationLabels.countries.DO },
            { value: 'EC', label: locationLabels.countries.EC },
            { value: 'EG', label: locationLabels.countries.EG },
            { value: 'SV', label: locationLabels.countries.SV },
            { value: 'GQ', label: locationLabels.countries.GQ },
            { value: 'ER', label: locationLabels.countries.ER },
            { value: 'EE', label: locationLabels.countries.EE },
            { value: 'ET', label: locationLabels.countries.ET },
            { value: 'FJ', label: locationLabels.countries.FJ },
            { value: 'FI', label: locationLabels.countries.FI },
            { value: 'FR', label: locationLabels.countries.FR },
            { value: 'GF', label: locationLabels.countries.GF },
            { value: 'PF', label: locationLabels.countries.PF },
            { value: 'GA', label: locationLabels.countries.GA },
            { value: 'GM', label: locationLabels.countries.GM },
            { value: 'GE', label: locationLabels.countries.GE },
            { value: 'DE', label: locationLabels.countries.DE },
            { value: 'GH', label: locationLabels.countries.GH },
            { value: 'GI', label: locationLabels.countries.GI },
            { value: 'GR', label: locationLabels.countries.GR },
            { value: 'GL', label: locationLabels.countries.GL },
            { value: 'GD', label: locationLabels.countries.GD },
            { value: 'GP', label: locationLabels.countries.GP },
            { value: 'GT', label: locationLabels.countries.GT },
            { value: 'GG', label: locationLabels.countries.GG },
            { value: 'GN', label: locationLabels.countries.GN },
            { value: 'GW', label: locationLabels.countries.GW },
            { value: 'GY', label: locationLabels.countries.GY },
            { value: 'HT', label: locationLabels.countries.HT },
            { value: 'HN', label: locationLabels.countries.HN },
            { value: 'HU', label: locationLabels.countries.HU },
            { value: 'IS', label: locationLabels.countries.IS },
            { value: 'IN', label: locationLabels.countries.IN },
            { value: 'ID', label: locationLabels.countries.ID },
            { value: 'IQ', label: locationLabels.countries.IQ },
            { value: 'IE', label: locationLabels.countries.IE },
            { value: 'IM', label: locationLabels.countries.IM },
            { value: 'IL', label: locationLabels.countries.IL },
            { value: 'IT', label: locationLabels.countries.IT },
            { value: 'JM', label: locationLabels.countries.JM },
            { value: 'JP', label: locationLabels.countries.JP },
            { value: 'JE', label: locationLabels.countries.JE },
            { value: 'JO', label: locationLabels.countries.JO },
            { value: 'KZ', label: locationLabels.countries.KZ },
            { value: 'KE', label: locationLabels.countries.KE },
            { value: 'KI', label: locationLabels.countries.KI },
            { value: 'KR', label: locationLabels.countries.KR },
            { value: 'KW', label: locationLabels.countries.KW },
            { value: 'KG', label: locationLabels.countries.KG },
            { value: 'LA', label: locationLabels.countries.LA },
            { value: 'LV', label: locationLabels.countries.LV },
            { value: 'LB', label: locationLabels.countries.LB },
            { value: 'LS', label: locationLabels.countries.LS },
            { value: 'LR', label: locationLabels.countries.LR },
            { value: 'LY', label: locationLabels.countries.LY },
            { value: 'LI', label: locationLabels.countries.LI },
            { value: 'LT', label: locationLabels.countries.LT },
            { value: 'LU', label: locationLabels.countries.LU },
            { value: 'MK', label: locationLabels.countries.MK },
            { value: 'MG', label: locationLabels.countries.MG },
            { value: 'MW', label: locationLabels.countries.MW },
            { value: 'MY', label: locationLabels.countries.MY },
            { value: 'MV', label: locationLabels.countries.MV },
            { value: 'ML', label: locationLabels.countries.ML },
            { value: 'MT', label: locationLabels.countries.MT },
            { value: 'MQ', label: locationLabels.countries.MQ },
            { value: 'MR', label: locationLabels.countries.MR },
            { value: 'MU', label: locationLabels.countries.MU },
            { value: 'YT', label: locationLabels.countries.YT },
            { value: 'MX', label: locationLabels.countries.MX },
            { value: 'MD', label: locationLabels.countries.MD },
            { value: 'MC', label: locationLabels.countries.MC },
            { value: 'MN', label: locationLabels.countries.MN },
            { value: 'ME', label: locationLabels.countries.ME },
            { value: 'MS', label: locationLabels.countries.MS },
            { value: 'MA', label: locationLabels.countries.MA },
            { value: 'MZ', label: locationLabels.countries.MZ },
            { value: 'MM', label: locationLabels.countries.MM },
            { value: 'NA', label: locationLabels.countries.NA },
            { value: 'NR', label: locationLabels.countries.NR },
            { value: 'NP', label: locationLabels.countries.NP },
            { value: 'NL', label: locationLabels.countries.NL },
            { value: 'NC', label: locationLabels.countries.NC },
            { value: 'NZ', label: locationLabels.countries.NZ },
            { value: 'NI', label: locationLabels.countries.NI },
            { value: 'NE', label: locationLabels.countries.NE },
            { value: 'NG', label: locationLabels.countries.NG },
            { value: 'NU', label: locationLabels.countries.NU },
            { value: 'NF', label: locationLabels.countries.NF },
            { value: 'NO', label: locationLabels.countries.NO },
            { value: 'OM', label: locationLabels.countries.OM },
            { value: 'PK', label: locationLabels.countries.PK },
            { value: 'PS', label: locationLabels.countries.PS },
            { value: 'PA', label: locationLabels.countries.PA },
            { value: 'PG', label: locationLabels.countries.PG },
            { value: 'PY', label: locationLabels.countries.PY },
            { value: 'PE', label: locationLabels.countries.PE },
            { value: 'PH', label: locationLabels.countries.PH },
            { value: 'PL', label: locationLabels.countries.PL },
            { value: 'PT', label: locationLabels.countries.PT },
            { value: 'QA', label: locationLabels.countries.QA },
            { value: 'RE', label: locationLabels.countries.RE },
            { value: 'RO', label: locationLabels.countries.RO },
            { value: 'RU', label: locationLabels.countries.RU },
            { value: 'RW', label: locationLabels.countries.RW },
            { value: 'SH', label: locationLabels.countries.SH },
            { value: 'WS', label: locationLabels.countries.WS },
            { value: 'SA', label: locationLabels.countries.SA },
            { value: 'SN', label: locationLabels.countries.SN },
            { value: 'RS', label: locationLabels.countries.RS },
            { value: 'SC', label: locationLabels.countries.SC },
            { value: 'SL', label: locationLabels.countries.SL },
            { value: 'SG', label: locationLabels.countries.SG },
            { value: 'SX', label: locationLabels.countries.SX },
            { value: 'SK', label: locationLabels.countries.SK },
            { value: 'SI', label: locationLabels.countries.SI },
            { value: 'SB', label: locationLabels.countries.SB },
            { value: 'SO', label: locationLabels.countries.SO },
            { value: 'ZA', label: locationLabels.countries.ZA },
            { value: 'SS', label: locationLabels.countries.SS },
            { value: 'ES', label: locationLabels.countries.ES },
            { value: 'LK', label: locationLabels.countries.LK },
            { value: 'SR', label: locationLabels.countries.SR },
            { value: 'SZ', label: locationLabels.countries.SZ },
            { value: 'SE', label: locationLabels.countries.SE },
            { value: 'CH', label: locationLabels.countries.CH },
            { value: 'TW', label: locationLabels.countries.TW },
            { value: 'TJ', label: locationLabels.countries.TJ },
            { value: 'TZ', label: locationLabels.countries.TZ },
            { value: 'TH', label: locationLabels.countries.TH },
            { value: 'TL', label: locationLabels.countries.TL },
            { value: 'TG', label: locationLabels.countries.TG },
            { value: 'TK', label: locationLabels.countries.TK },
            { value: 'TO', label: locationLabels.countries.TO },
            { value: 'TT', label: locationLabels.countries.TT },
            { value: 'TN', label: locationLabels.countries.TN },
            { value: 'TR', label: locationLabels.countries.TR },
            { value: 'TM', label: locationLabels.countries.TM },
            { value: 'UG', label: locationLabels.countries.UG },
            { value: 'UA', label: locationLabels.countries.UA },
            { value: 'AE', label: locationLabels.countries.AE },
            { value: 'GB', label: locationLabels.countries.GB },
            { value: 'UY', label: locationLabels.countries.UY },
            { value: 'UZ', label: locationLabels.countries.UZ },
            { value: 'VU', label: locationLabels.countries.VU },
            { value: 'VE', label: locationLabels.countries.VE },
            { value: 'VN', label: locationLabels.countries.VN },
            { value: 'VG', label: locationLabels.countries.VG },
            { value: 'EH', label: locationLabels.countries.EH },
            { value: 'YE', label: locationLabels.countries.YE },
            { value: 'ZM', label: locationLabels.countries.ZM },
            { value: 'ZW', label: locationLabels.countries.ZW },
        ],
    };
};

const getFiltersToExpand = (expanded: string[]) => {
    return {
        category: expanded.length === 0 || expanded.includes(SectionNames.CATEGORY),
        agentforce: expanded.includes(SectionNames.AGENTFORCE),
        tech: expanded.includes(SectionNames.TECH),
        product: expanded.includes(SectionNames.PRODUCT),
        labs: expanded.includes(SectionNames.SALESFORCE_LABS),
        industry: expanded.includes(SectionNames.INDUSTRY),
        impact: expanded.includes(SectionNames.IMPACT),
    };
};

const appSections = (
    type: string,
    filterParams: FilterParams | undefined,
    labels: FilterLabels,
    expanded: string[],
): FilterSection[] => {
    const appList = [];

    const toExpand = getFiltersToExpand(expanded);

    appList.push(priceAppFilterSectionOAF(filterParams, labels));
    appList.push(ratingAppFilterSection(filterParams, labels.filters));
    appList.push(agentForceFilterSection(filterParams, labels.oaf, labels.listingDetails, toExpand.agentforce));
    appList.push(categoryAppFilterSection(filterParams, labels.oaf, toExpand.category));
    appList.push(techAppFilterSection(filterParams, labels.filters, toExpand.tech));
    appList.push(productAppFilterSection(filterParams, labels.filters, toExpand.product));
    appList.push(labsFilterSection(filterParams, labels.filters, toExpand.labs));
    appList.push(featuresAppFilterSection(filterParams, labels.filters));
    appList.push(editionAppFilterSection(filterParams, labels.filters));
    appList.push(industryFilterSection(filterParams, labels, toExpand.industry));
    appList.push(targetUserPersonaFilterSection(filterParams, labels));
    appList.push(languageFilterSection(filterParams, labels.filters));
    appList.push(impactAppFilterSection(filterParams, labels.filters, type, toExpand.impact));
    // @ts-expect-error * Automated Type Error Fix
    return appList;
};

const externalAgentSections = (filterParams: FilterParams | undefined, labels: FilterLabels): FilterSection[] => [
    externalAgentFilterSection(filterParams, labels.filters),
    domainFilterSection(filterParams, labels.filters),
];

const agentSections = (filterParams: FilterParams | undefined, labels: FilterLabels) => {
    const appList = [];
    appList.push(agentForceFilterSection(filterParams, labels.oaf, labels.listingDetails, true));
    appList.push(categoryAppFilterSection(filterParams, labels.oaf));
    return appList;
};

const achievementConsultantFilterSection = (
    labels: FilterLabels,
    filterParams: FilterParams | undefined,
    expanded: string[],
): FilterSection => {
    return {
        label: labels.filters.partnerAchievements.label,
        name: SectionNames.ACHIEVEMENT,
        key: 'achievement-consultant',
        statusBadge: FeatureFlags.FF_PARTNER_ACHIEVEMENTS_FILTERS_NEW_BADGE ? StatusBadgeVariant.New : undefined,
        multiple: true,
        selectedValue: filterParams?.achievement ?? [],
        toggleDefault: expanded.includes(SectionNames.ACHIEVEMENT) || Boolean(filterParams?.achievement?.length),
        options: [
            {
                label: labels.filters.partnerAchievements.summit,
                value: SUMMIT_ACHIEVEMENT_FILTER_VALUE,
            },
            {
                label: labels.listingDetails.achievements.partnerOfYear.title,
                value: PARTNER_OF_YEAR_ACHIEVEMENT_FILTER_VALUE,
            },
            {
                label: labels.listingDetails.achievements.fdePartner.title,
                value: FDE_ACHIEVEMENT_FILTER_VALUE,
            },
        ],
    };
};

const consultantSections = (
    type: string,
    filterParams: FilterParams | undefined,
    labels: FilterLabels,
    expanded: string[],
): FilterSection[] => {
    const consultantList = [];
    consultantList.push({
        ...sfExpertiseConsultantFilterSection(filterParams, labels.filters),
        toggleDefault: expanded.includes('sfExpertise'),
    });
    consultantList.push({
        ...industryExpertiseConsultantFilterSection(filterParams, labels.filters),
        toggleDefault: expanded.includes('industryExpertise'),
    });
    if (isConsultantAchievementFilterEnabled(type)) {
        consultantList.push(achievementConsultantFilterSection(labels, filterParams, expanded));
    }
    consultantList.push({
        ...noOfExpertsConsultantFilterSection(filterParams, labels.filters),
        toggleDefault: expanded.includes('experts'),
    });
    consultantList.push(locationConsultantFilterSection(filterParams, labels.filters, labels.location));
    consultantList.push(ratingAppFilterSection(filterParams, labels.filters));
    consultantList.push(languageFilterSection(filterParams, labels.filters));
    consultantList.push(impactAppFilterSection(filterParams, labels.filters, type));

    // @ts-expect-error * Automated Type Error Fix
    return consultantList;
};

const priceSlackFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: labels.price.slackLabel || 'Price',
        name: SectionNames.PRICE,
        multiple: true,
        selectedValue: filterParams?.price,
        key: 'price-slack',
        options: [
            { label: labels.price.free, value: 'free' },
            { label: labels.price.freemium, value: 'freemium' },
            { label: labels.price.paid, value: 'paid' },
            { label: labels.price.slackFreeToInstall, value: 'free_to_install' },
        ],
    };
};

const categorySlackFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: labels.slackCategories.label,
        name: SectionNames.CATEGORY,
        multiple: true,
        selectedValue: filterParams?.category,
        key: 'category-slack',
        options: [
            { label: labels.slackCategories.productivity, value: 'productivity' },
            { label: labels.slackCategories.collaboration, value: 'collaboration' },
            {
                label: labels.slackCategories.itAndAdministration,
                value: 'itAndAdministration',
            },
            {
                label: labels.slackCategories.enterpriseResourcePlanning,
                value: 'enterpriseResourcePlanning',
            },
            {
                label: labels.slackCategories.customerService,
                value: 'customerService',
            },
            { label: labels.slackCategories.analytics, value: 'analytics' },
            { label: labels.slackCategories.sales, value: 'sales' },
            { label: labels.slackCategories.marketing, value: 'marketing' },
            { label: labels.slackCategories.finance, value: 'finance' },
        ],
    };
};

// Slack uses a fixed set of languages; differs from apps which derives languages from listing schema
const languageSlackFilterSection = (filterParams: FilterParams | undefined, labels: SpecificFilterLabels) => {
    return {
        label: labels.language.label,
        name: SectionNames.LANGUAGES,
        multiple: true,
        selectedValue: filterParams?.languages,
        key: 'languages-slack',
        options: [
            { label: labels.language.en, value: 'en' },
            { label: labels.language.ja, value: 'ja' },
            { label: labels.language.fr, value: 'fr' },
            { label: labels.language.de, value: 'de' },
            { label: labels.language.ar, value: 'ar' },
        ],
    };
};

const slackSections = (filterParams: FilterParams | undefined, labels: FilterLabels): FilterSection[] => {
    const slackList = [];
    slackList.push(categorySlackFilterSection(filterParams, labels.filters));
    slackList.push(priceSlackFilterSection(filterParams, labels.filters));
    slackList.push(languageSlackFilterSection(filterParams, labels.filters));

    // @ts-expect-error * Automated Type Error Fix
    return slackList;
};

/** Optional presentation data supplied by the owning store, independently of its data source. */
export type FilterSectionOverrides = Partial<
    Record<keyof FilterParams, Pick<FilterSection, 'options'> & Partial<Pick<FilterSection, 'type' | 'label'>>>
>;

const filterOptionsForType = (
    type: string,
    filterParams: FilterParams | undefined,
    labels: FilterLabels,
    expanded: string[] = [],
    overrides: FilterSectionOverrides = {},
) => {
    const filterOptions = {
        apps: appSections(type, filterParams, labels, expanded),
        consultants: consultantSections(type, filterParams, labels, expanded),
        agents: agentSections(filterParams, labels),
        slack: slackSections(filterParams, labels),
        externalAgents: externalAgentSections(filterParams, labels),
        businessneeds: [categoryAppFilterSection(filterParams, labels.oaf)],
        agentforce: [
            agentForceFilterSection(filterParams, labels.oaf, labels.listingDetails, true),
            categoryAppFilterSection(filterParams, labels.oaf),
        ],
        products: [productExploreFilterSection(filterParams, labels.filters, true)],
        industries: [industryFilterSection(filterParams, labels, true)],
        labs: [techAppFilterSection(filterParams, labels.filters, true)],
        impact: [impactAppExploreFilterSection(filterParams, labels.filters, 'apps', true)],
    };

    // @ts-expect-error * Automated Type Error Fix
    const sections: FilterSection[] | undefined = filterOptions[type || 'apps'];
    return (sections ?? []).map((section) => {
        const override = overrides[section.name as keyof FilterParams];
        return override ? { ...section, ...override } : section;
    });
};

const sortOptionsForType = (type: string, sortLabels: I18nMessages['search']['sort']): object[] => {
    const isSlackListingsActive = isSlackListingsEnabled();
    const sortOptionsByType = {
        apps: [
            { value: 'relevant', label: sortLabels.relevant },
            { value: 'rating', label: sortLabels.rating },
            { value: 'popularity', label: sortLabels.popularity },
            { value: 'releaseDate', label: sortLabels.releaseDate },
            { value: 'reviews', label: sortLabels.reviews },
            { value: 'name', label: sortLabels.name },
            { value: 'provider', label: sortLabels.provider },
        ],
        slack: [
            { value: 'relevant', label: sortLabels.relevant },
            ...(!isSlackListingsActive
                ? [
                      { value: 'rating', label: sortLabels.rating },
                      { value: 'popularity', label: sortLabels.popularity },
                      { value: 'releaseDate', label: sortLabels.releaseDate },
                      { value: 'reviews', label: sortLabels.reviews },
                  ]
                : []),
            { value: 'name', label: sortLabels.name },
            ...(!isSlackListingsActive ? [{ value: 'provider', label: sortLabels.provider }] : []),
        ],
        consultants: [
            { value: 'relevant', label: sortLabels.relevant },
            { value: 'rating', label: sortLabels.rating },
            {
                value: 'experts',
                label: isConsistentFiltersEnabled() ? sortLabels.experts : sortLabels.expertsLegacy,
            },
            { value: 'popularity', label: sortLabels.popularity },
        ],
        agents: [
            { value: 'relevant', label: sortLabels.relevant },
            { value: 'rating', label: sortLabels.rating },
            { value: 'popularity', label: sortLabels.popularity },
            { value: 'releaseDate', label: sortLabels.releaseDate },
            { value: 'reviews', label: sortLabels.reviews },
            { value: 'name', label: sortLabels.name },
            { value: 'provider', label: sortLabels.provider },
        ],
        externalAgents: [
            { value: 'relevant', label: sortLabels.relevant },
            { value: 'name', label: sortLabels.name },
        ],
        favorites: [
            { value: 'mostRecent', label: sortLabels.mostRecent },
            { value: 'rating', label: sortLabels.rating },
            { value: 'name', label: sortLabels.name },
            { value: 'provider', label: sortLabels.provider },
        ],
    };
    // @ts-expect-error * Automated Type Error Fix
    return sortOptionsByType[type || 'apps'];
};

const SUMMARIZE_SORT_FIELD: Record<string, string> = {
    rating: 'Rating',
    popularity: 'Popularity',
    releaseDate: 'Release Date',
    reviews: 'Reviews',
    name: 'Name',
    provider: 'Publisher Name',
    mostRecent: 'Most Recent',
    experts: 'Number of Experts',
};

const SUMMARIZE_ASCENDING_SORTS = new Set(['name', 'provider']);

const summarizeSortForValue = (
    sort: string | undefined,
): { field: string; direction: 'Ascending' | 'Descending' } | undefined => {
    if (!sort || sort === 'relevant') {
        return undefined;
    }
    const field = SUMMARIZE_SORT_FIELD[sort];
    if (!field) {
        return undefined;
    }
    return {
        field,
        direction: SUMMARIZE_ASCENDING_SORTS.has(sort) ? 'Ascending' : 'Descending',
    };
};

// @ts-expect-error * Automated Type Error Fix
const isOptionChecked = (value, option): boolean => {
    return (
        (typeof value === 'string' && value === (option.value || option.label)) ||
        (Array.isArray(value) && value.includes(option.value))
    );
};

export { featuresAppFilterSection, filterOptionsForType, isOptionChecked, sortOptionsForType, summarizeSortForValue };
